/**
 * @copyright Copyright 2021 Epic Systems Corporation
 * @file Summary of whether the app is approved in non PRD, PRD, or both and when it was marked for each (used by the In Progress Apps widget)
 * @module Epic.AppOrchard.Dashboard.DownloadApprovedInfo
 */

import { CheckIcon } from "@chakra-ui/icons";
import { Box } from "@chakra-ui/react";
import { WidgetTag } from "ao/components/Dashboard/widgetHelpers";
import React, { FC, memo } from "react";

interface IProps {
	nonProdLicensedOn: Date | null;
	prodLicensedOn: Date | null;
	/**  Whether the download is activated in Prod only. This can happen if it was approved for all environments, 
		and later on a different version was approved for just non PRD */
	enabledForProdOnly: boolean;
}

/** Summary of whether the app is approved in non PRD, PRD, or both and when it was marked for each (used by the In Progress Apps widget) */
export const DownloadApprovedInfo: FC<IProps> = memo((props: IProps) => {
	const { nonProdLicensedOn, prodLicensedOn, enabledForProdOnly } = props;

	if (nonProdLicensedOn && !prodLicensedOn) {
		return (
			<WidgetTag
				label="Enabled for non-production"
				tooltipContent={`Enabled for non-production on ${nonProdLicensedOn.toLocaleDateString()}`}
				icon={CheckIcon}
			></WidgetTag>
		);
	} else if (nonProdLicensedOn && prodLicensedOn) {
		return (
			<>
				<WidgetTag
					label={enabledForProdOnly ? "Enabled for production" : "Enabled for all environments"}
					tooltipContent={
						<>
							{!enabledForProdOnly && (
								<Box>
									Enabled for non-production on {nonProdLicensedOn.toLocaleDateString()}
								</Box>
							)}
							<Box>Enabled for production on {prodLicensedOn.toLocaleDateString()}</Box>
						</>
					}
					icon={CheckIcon}
				></WidgetTag>
			</>
		);
	} else if (!nonProdLicensedOn && prodLicensedOn) {
		// PRD only => activation was for all kinds of environments at once
		return (
			<WidgetTag
				label={enabledForProdOnly ? "Enabled for production" : "Enabled for all environments"}
				tooltipContent={`Enabled for ${
					enabledForProdOnly ? "production" : "all environments"
				} on ${prodLicensedOn.toLocaleDateString()}`}
				icon={CheckIcon}
			></WidgetTag>
		);
	} else {
		return null;
	}
});
